import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Users as UsersIcon, Eye } from 'lucide-react';
import apiService from '../../services/api';
import useToast from '../../hooks/useToast';
import { usePermissionContext } from '../../contexts/PermissionContext';
import ReactDOM from 'react-dom';
import ConfirmModal from '../ConfirmModal';
const UsersList = ({ onView, currentPage, setCurrentPage, itemsPerPage }) => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const { hasPermission, permissions } = usePermissionContext();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [businessId, setBusinessId] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: null });
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: '', data: null });

    useEffect(() => {
        loadUsers();
    }, [refreshKey]);

    const loadUsers = async () => {
        try {
            setLoading(true);
            const userDetails = await apiService.getUserDetails();
            let bid = userDetails?.business_id;
            
            if (!bid) {
                console.warn('Business ID is undefined, using default value 40');
                bid = 40;
            }
            
            setBusinessId(bid);

            console.log('Loading users for business_id:', bid);
            const response = await apiService.getUsers({
                business_id: bid
            });
            console.log('Users API response:', response);
            console.log('Response type:', typeof response);
            console.log('Response keys:', response ? Object.keys(response) : 'null');
            
            // Handle different response structures
            let usersData = [];
            
            if (response && response.data) {
                // Check if data is an array or has a data property
                if (Array.isArray(response.data)) {
                    usersData = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    usersData = response.data.data;
                }
            } else if (Array.isArray(response)) {
                usersData = response;
            }
            
            console.log('Final users data:', usersData);
            console.log('Is array:', Array.isArray(usersData));
            setUsers(Array.isArray(usersData) ? usersData : []);
        } catch (error) {
            console.error('Error loading users:', error);
            alert('Failed to load users: ' + error.message + '\n\nPlease check:\n1. Is the API endpoint configured?\n2. Do you have permission to view users?\n3. Check browser console for details.');
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async (user) => {
        if (permissions.length > 0 && !hasPermission('user.update')) {
            showError('You do not have permission to edit users');
            return;
        }
        // Navigate instantly using the list data
        navigate('/users/add', { state: { user } });
    };

    const handleDelete = async (user) => {
        if (permissions.length > 0 && !hasPermission('user.delete')) {
            showError('You do not have permission to delete users');
            return;
        }
        setOpenDropdownId(null);
        setConfirmModal({
            isOpen: true,
            action: 'delete',
            data: user
        });
    };

    const confirmAction = async () => {
        const { action, data } = confirmModal;
        setConfirmModal({ isOpen: false, action: '', data: null });

        if (action === 'delete') {
            try {
                const response = await apiService.deleteUser(data.id);
                if (response.success) {
                    showSuccess('User deleted successfully!');
                    loadUsers(); // Refresh the user list
                } else {
                    showError(response.message || 'Failed to delete user');
                }
            } catch (error) {
                console.error('Error deleting user:', error);
                showError(error.message || 'Failed to delete user');
            }
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedUsers = (usersToSort) => {
        if (!sortConfig.key) return usersToSort;

        return [...usersToSort].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
                aValue = parseFloat(aValue);
                bValue = parseFloat(bValue);
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const toggleDropdown = (userId, event) => {
        event.stopPropagation();
        
        if (openDropdownId === userId) {
            setOpenDropdownId(null);
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const dropdownHeight = 120; // Approximate height of dropdown
        const viewportHeight = window.innerHeight;
        
        let top = null;
        let bottom = null;
        
        if (rect.bottom + dropdownHeight <= viewportHeight) {
            top = rect.bottom + window.scrollY;
        } else {
            bottom = viewportHeight - rect.top + window.scrollY;
        }
        
        setDropdownPosition({
            top,
            bottom,
            left: rect.left + window.scrollX
        });
        
        setOpenDropdownId(userId);
    };

    useEffect(() => {
        const handleClickOutside = () => setOpenDropdownId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Show loading state with uniform UI
    if (loading) {
        return (
            <div className="loading-sales">
                <h3>⏳ Loading Users...</h3>
                <p>Fetching users data from server...</p>
            </div>
        );
    }

    const sortedUsers = getSortedUsers(users);
    const totalItems = sortedUsers.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedUsers = sortedUsers.slice(startIndex, startIndex + itemsPerPage);

    return (
        <div>
            <table className="products-table">
                <thead>
                    <tr>
                        <th>Action</th>
                        {[
                            { key: 'username', label: 'Username' },
                            { key: 'first_name', label: 'Full Name' },
                            { key: 'email', label: 'Email' },
                            { key: 'role', label: 'Role' },
                            { key: 'status', label: 'Status' }
                        ].map(({ key, label }) => (
                            <th key={key} className="sortable" onClick={() => handleSort(key)}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                    {label}
                                    {sortConfig.key === key ? (
                                        sortConfig.direction === 'asc' ? (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                                <path d="M12 19V5M5 12l7-7 7 7" />
                                            </svg>
                                        ) : (
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--etpos-primary)' }}>
                                                <path d="M12 5v14M5 12l7 7 7-7" />
                                            </svg>
                                        )
                                    ) : (
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M7 15l5 5 5-5" />
                                            <path d="M7 9l5-5 5 5" />
                                        </svg>
                                    )}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {paginatedUsers.length === 0 ? (
                        <tr>
                            <td colSpan="6" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                No users found. Create your first user to get started.
                            </td>
                        </tr>
                    ) : (
                        paginatedUsers.map((user) => (
                            <tr key={user.id}>
                                <td className="actions-cell">
                                    <div className="action-dropdown-container">
                                        <button
                                            className="action-btn-trigger"
                                            onClick={(e) => toggleDropdown(user.id, e)}
                                        >
                                            Actions <span className="arrow-down">▼</span>
                                        </button>
                                        {openDropdownId === user.id && ReactDOM.createPortal(
                                            <div
                                                className="action-dropdown-menu"
                                                style={{
                                                    position: 'fixed',
                                                    top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                                    bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                                    left: `${dropdownPosition.left}px`,
                                                    zIndex: 1000
                                                }}
                                            >
                                                <button onClick={() => {
                                                    setOpenDropdownId(null);
                                                    onView && onView(user);
                                                }}>
                                                    <Eye size={14} /> View
                                                </button>
                                                {(permissions.length === 0 || hasPermission('user.update')) && (
                                                    <button onClick={() => handleEdit(user)}>
                                                        <Edit size={14} /> Edit
                                                    </button>
                                                )}
                                                {(permissions.length === 0 || hasPermission('user.delete')) && (
                                                    <button onClick={() => handleDelete(user)}>
                                                        <Trash2 size={14} /> Delete
                                                    </button>
                                                )}
                                            </div>,
                                            document.body
                                        )}
                                    </div>
                                </td>
                                <td>{user.username || user.email}</td>
                                <td>{user.first_name && user.last_name 
                                    ? `${user.first_name} ${user.last_name}` 
                                    : user.full_name || '-'}</td>
                                <td>{user.email || '-'}</td>
                                <td>{user.role?.name || user.role_name || '-'}</td>
                                <td>
                                    {user.status === 'active' || user.is_active ? (
                                        <span style={{ 
                                            padding: '0.25rem 0.5rem', 
                                            backgroundColor: '#dcfce7', 
                                            color: '#166534', 
                                            borderRadius: '4px', 
                                            fontSize: '0.75rem',
                                            fontWeight: '500'
                                        }}>
                                            Active
                                        </span>
                                    ) : (
                                        <span style={{ 
                                            padding: '0.25rem 0.5rem', 
                                            backgroundColor: '#fee2e2', 
                                            color: '#991b1b', 
                                            borderRadius: '4px', 
                                            fontSize: '0.75rem',
                                            fontWeight: '500'
                                        }}>
                                            Inactive
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {totalItems > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', marginTop: '0.5rem', borderTop: '1px solid #e2e8f0' }}>
                    <div style={{ color: '#334155', fontSize: '0.9rem', fontWeight: '500' }}>
                        Showing {startIndex + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', color: '#334155', fontWeight: '500', fontSize: '0.95rem' }}>
                            Page {currentPage} of {totalPages || 1}
                        </span>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.action === 'delete' ? 'Delete User' : 'Confirm Action'}
                message={confirmModal.action === 'delete' ? `Are you sure you want to delete user "${confirmModal.data?.username || confirmModal.data?.email}"?` : 'Are you sure?'}
                onConfirm={confirmAction}
                onCancel={() => setConfirmModal({ isOpen: false, action: '', data: null })}
                confirmText={confirmModal.action === 'delete' ? 'Delete' : 'Confirm'}
            />
        </div>
    );
};

export default UsersList;
